import { useQuery } from "@apollo/client/react";
import { DataGridProProps, GridEventListener, GridRowParams, GridSortModel } from "@mui/x-data-grid-pro";
import { useEffect, useMemo, useState } from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { DownloadsDataGrid } from "../../components/DownloadsDataGrid/DownloadsDataGrid";
import {
  DownloadBundle,
  DownloadPart,
  DownloadsDocument,
  DownloadsQuery,
  DownloadsQueryVariables,
  PublishedSourceBundlesDocument,
  PublishedSourceBundlesQuery,
  PublishedSourceBundlesQueryVariables,
} from "../../graphql/types";

import { styled, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { getSortBy, getSortDirection } from "../../components/DataGridUtils";
import { GqlError } from "../../components/common/GqlError";
import useAuth, { DataGridColumns } from "../../context/UserContext";
import { setOrRemoveRangeParam, setOrRemoveSearchParam } from "../../lib/Util";
import {
  UploadsForDownloadDrawer,
  UploadsForDownloadDrawerProps,
} from "../../pages/downloads/UploadsForDownloadDrawer";
import { DownloadFilters } from "./DownloadFilters";
import {
  getDefaultDownloadFilters,
  getDownloadsDatagridQueryVariables,
  getDownloadsNodes,
  IDownloadFilters,
} from "./downloadsDatagridUtils";
const Container = styled(Stack)({
  width: "100%",
  borderRadius: "8px",
  height: "70vh",
});

const initialVisibilityModelDownloadsDatagrid: Partial<Record<DataGridColumns, boolean>> = {
  [DataGridColumns.ReportDateTime]: true,
  [DataGridColumns.Started]: true,
  [DataGridColumns.Project]: true,
  [DataGridColumns.Name]: true,
  [DataGridColumns.Purpose]: true,
  [DataGridColumns.Description]: true,
  [DataGridColumns.Status]: true,
  [DataGridColumns.Actions]: true,
  [DataGridColumns.Created]: false,
  [DataGridColumns.Finished]: false,
  [DataGridColumns.DownloadDuration]: false,
  [DataGridColumns.QueuedDuration]: false,
};

const updateSearchParams = (
  searchParams: URLSearchParams,
  filter: IDownloadFilters,
  setSearchParams: SetURLSearchParams
) => {
  const {
    projects,
    importNames,
    bundleNames,
    purpose,
    search,
    status,
    reportRange,
    downloadedRange,
    sortBy,

    sortDirection,
  } = filter;
  const updatedParams = new URLSearchParams(searchParams.toString());
  setOrRemoveSearchParam("projects", projects?.join(","), updatedParams);
  setOrRemoveSearchParam("downloadNames", importNames?.join(","), updatedParams);
  setOrRemoveSearchParam("bundleNames", bundleNames?.join(","), updatedParams);
  setOrRemoveSearchParam("purpose", purpose, updatedParams);
  setOrRemoveSearchParam("search", search, updatedParams);
  setOrRemoveSearchParam("status", status, updatedParams);
  setOrRemoveSearchParam("sortDirection", sortDirection, updatedParams);
  setOrRemoveSearchParam("sortBy", sortBy, updatedParams);
  setOrRemoveRangeParam("start", "end", updatedParams, reportRange);
  setOrRemoveRangeParam("downloaded_start", "downloaded_end", updatedParams, downloadedRange);
  if (!updatedParams.get("currentTab")) {
    updatedParams.set("currentTab", "downloads-list-tab");
  }
  if (updatedParams.toString() !== searchParams.toString()) setSearchParams(updatedParams);
};

const getDrawerProps = (row?: DownloadBundle): Omit<UploadsForDownloadDrawerProps, "handleCloseDrawer"> | undefined => {
  if (!row) return undefined;
  if (row?.parts?.length === 1 && !!row.parts[0]) {
    const part: DownloadPart = row.parts[0];

    const areAllRowsDirty = part.row_count === part.dirty_row_count && (part?.dirty_row_count || 0) > 0;
    return {
      downloadPartIds: [part.id],
      areAllRowsDirty: areAllRowsDirty,
      downloadName: part.name || part.source_table.name,
      hasConnectedUploadDeclarations: part.hasConnectedUploadDeclarations || false,
      downloadStatus: row.status,
    };
  } else if (row?.parts && row.parts.length > 0 && row.parts[0]) {
    const { partsWithAllDirtyRows, hasConnectedUploadDeclarations, downloadName, partIds } = row.parts
      .filter((part): part is DownloadPart => part != null)
      .reduce(
        (acc, part) => {
          if (part.row_count === part.dirty_row_count) acc.partsWithAllDirtyRows++;
          acc.hasConnectedUploadDeclarations =
            part.hasConnectedUploadDeclarations || acc.hasConnectedUploadDeclarations;
          acc.downloadName = acc.downloadName || part.name || part.source_table.name;
          acc.partIds.push(part.id);
          return acc;
        },
        {
          partsWithAllDirtyRows: 0,
          hasConnectedUploadDeclarations: false,
          downloadName: "",
          partIds: [] as string[],
        }
      );
    return {
      downloadPartIds: partIds,
      areAllRowsDirty: partsWithAllDirtyRows === row.parts.length,
      downloadName: downloadName,
      hasConnectedUploadDeclarations: hasConnectedUploadDeclarations,
      downloadStatus: row.status,
    };
  }
};

const DownloadsDataGridWithQuery = ({
  excludeFields,
  variables,
  useDefaultDateRange = true,
  disableDataGridFilter,
}: {
  excludeFields?: string[];
  variables?: Partial<IDownloadFilters>;
  useDefaultDateRange?: boolean;
  disableDataGridFilter?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [filter, setFilter] = useState<IDownloadFilters>(
    getDefaultDownloadFilters(searchParams, useDefaultDateRange, variables)
  );
  const [prevInputsKey, setPrevInputsKey] = useState(
    () => `${searchParams.toString()}|${useDefaultDateRange}|${JSON.stringify(variables)}`
  );
  useEffect(() => {
    updateSearchParams(searchParams, filter, setSearchParams);
  }, [filter, searchParams, setSearchParams]);

  if (variables) {
    const currentInputsKey = `${searchParams.toString()}|${useDefaultDateRange}|${JSON.stringify(variables)}`;
    if (currentInputsKey !== prevInputsKey) {
      setPrevInputsKey(currentInputsKey);
      setFilter(getDefaultDownloadFilters(searchParams, useDefaultDateRange, variables));
    }
  }

  const queryVariables = getDownloadsDatagridQueryVariables(filter);

  const { data, loading, fetchMore, error, refetch } = useQuery<DownloadsQuery, DownloadsQueryVariables>(
    DownloadsDocument,
    {
      variables: queryVariables,
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    }
  );
  const treeDataQuery = useQuery<PublishedSourceBundlesQuery, PublishedSourceBundlesQueryVariables>(
    PublishedSourceBundlesDocument
  );

  const onRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = () => {
    if (!data?.downloads?.pageInfo.hasNextPage) return;
    fetchMore({
      variables: {
        after: data?.downloads?.pageInfo?.endCursor || undefined,
      },
    });
  };

  const onSortModelChange = (model: GridSortModel) => {
    const field = model?.[0]?.field;
    const direction = model?.[0]?.sort;
    const sortBy = getSortBy(field);
    const sortDirection = getSortDirection(direction);
    if (sortBy === filter.sortBy && sortDirection === filter.sortDirection) return;
    refetch({
      sortBy,
      sortDirection,
    });
    setFilter({
      ...filter,
      sortBy,
      sortDirection,
    });
  };

  const handleRowClick: GridEventListener<"rowClick"> | undefined = ({ row }: GridRowParams<DownloadBundle>) => {
    setSearchParams((urlParams) => {
      urlParams.set("downloadBundleId", row.id);
      return urlParams;
    });
  };

  const handleCloseDrawer = () => {
    setSearchParams((urlParams) => {
      urlParams.delete("downloadBundleId");
      return urlParams;
    });
  };

  const rows: DownloadBundle[] = data?.downloads?.edges.map((edge) => edge.node as DownloadBundle) || [];

  const drawerProps = getDrawerProps(rows.find((row) => row.id === searchParams.get("downloadBundleId")));

  const { selectedNodes, nonSelectedNodes } = useMemo(
    () => getDownloadsNodes(treeDataQuery.data, filter),
    [treeDataQuery.data, filter]
  );

  return (
    <>
      {error ? (
        <GqlError error={error} />
      ) : (
        <>
          {disableDataGridFilter ? null : (
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: "28px" }}>
              <Stack direction="row" sx={{ gap: "16px", alignItems: "center" }}>
                <CloudArrowDownIcon size={32} />
                <Typography variant="heading.three">Downloads</Typography>
              </Stack>

              <DownloadFilters
                loading={treeDataQuery.loading}
                filter={filter}
                setFilter={setFilter}
                queryVariables={queryVariables}
                nonSelectedNodesInFilter={nonSelectedNodes}
                selectedNodesInFilter={selectedNodes}
                excludeFields={excludeFields}
                initialVisibilityModel={initialVisibilityModelDownloadsDatagrid}
                datagridPreferencesElement={user.datagridPreferences.downloads}
                preferenceElementToUpdate="downloads"
              />
            </Stack>
          )}
          <Container>
            <DownloadsDataGrid
              datagridPreferencesElement={user.datagridPreferences.downloads}
              rows={rows}
              onRowsScrollEnd={onRowsScrollEnd}
              filter={filter}
              loading={loading}
              onSortModelChange={onSortModelChange}
              excludeFields={excludeFields}
              handleRowClick={handleRowClick}
              initialVisibilityModel={initialVisibilityModelDownloadsDatagrid}
              preferenceElementToUpdate="downloads"
            />
            {searchParams.get("downloadBundleId") && drawerProps ? (
              <UploadsForDownloadDrawer handleCloseDrawer={handleCloseDrawer} {...drawerProps} />
            ) : null}
          </Container>
        </>
      )}
    </>
  );
};
export { DownloadsDataGridWithQuery, initialVisibilityModelDownloadsDatagrid };
